import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Dropdown } from 'semantic-ui-react'
import i18n from '~/components/i18n'
import Layout from '~/components/Layout'
import TableComponent from '~/components/TableComponent'
import { FarmersQuery } from '~/QueryHooks/Farmers'
import { useAuth } from '~/routes/components/AuthProvider'

const swissTableColumnHeaders = [
  {
    name: i18n.t('Tour data id'),
    key: 'uniqueId',
    sorted: 'desc'
  },
  {
    name: i18n.t('Date'),
    key: 'dateStart',
    type: 'date',
    format: 'DD.MM.YYYY HH:mm',
    sorted: 'desc'
  },
  {
    name: i18n.t('Date start'),
    key: 'timeFrom'
  },
  {
    name: i18n.t('Date end'),
    key: 'timeTo'
  },
  {
    name: i18n.t('Amount accepted (in l)'),
    key: 'amount',
    type: 'no-unit-number',
    decimalScale: 0,
    sorted: 'desc'
  },
  {
    name: i18n.t('Acceptance quantity (in kg)'),
    key: 'amountInKg',
    type: 'no-unit-number',
    decimalScale: 0,
    sorted: 'desc'
  },
  {
    name: i18n.t('avg. Temp. (°C)'),
    key: 'avgTemp',
    type: 'no-unit-number',
    decimalScale: 1,
    sorted: 'desc'
  },
  {
    name: i18n.t('Sample bottle'),
    key: 'sampleBottleNumber'
  },
  {
    name: i18n.t('Sample Compartment Temp (°C)'),
    key: 'sampleAvgTemp',
    type: 'no-unit-number',
    decimalScale: 1,
    sorted: 'desc'
  }
]

const defaultTableComunHeaders = [
  {
    name: i18n.t('Tour data id'),
    key: 'uniqueId'
  },
  {
    name: i18n.t('Date start'),
    type: 'date',
    format: 'DD.MM.YYYY HH:mm',
    key: 'date'
  },
  {
    name: i18n.t('Date end'),
    type: 'date',
    format: 'DD.MM.YYYY HH:mm',
    key: 'dateEnd'
  },
  {
    name: i18n.t('Amount accepted (in l)'),
    key: 'amount',
    type: 'no-unit-number',
    decimalScale: 0
  },
  {
    name: i18n.t('Time from'),
    key: 'timeFrom'
  },
  {
    name: i18n.t('Time to'),
    key: 'timeTo'
  },
  {
    name: i18n.t('Supplier No.'),
    key: 'supplierNo',
    type: 'supplier-link'
  },
  {
    name: i18n.t('Amount accepted (in l)'),
    key: 'amount',
    type: 'no-unit-number',
    decimalScale: 0
  },
  {
    name: i18n.t('avg. Temp. (°C)'),
    key: 'avgTemp',
    type: 'no-unit-number',
    decimalScale: 1
  },
  {
    name: i18n.t('Sample bottle'),
    key: 'sampleBottleNumber'
  },
  {
    name: i18n.t('Sample Compartment Temp (°C)'),
    key: 'sampleAvgTemp',
    type: 'no-unit-number',
    decimalScale: 1
  }
]

const getDefaultTableComunHeaders = (isSwissDairy: boolean) =>
  isSwissDairy ? swissTableColumnHeaders : defaultTableComunHeaders

const MilkDeliveries: React.FC = () => {
  const { t } = useTranslation()
  const { id } = useParams()
  const { systemData: { chEnv } } = useAuth()
  const isSwissDairy = chEnv === 1

  const [dataFiltered, setDataFiltered] = useState({
    rows: 10,
    page: 0,
    date: ''
  })

  const [tableColumnHeaders, setTableColumnHeaders] = useState(getDefaultTableComunHeaders(isSwissDairy))

  const {
    data: milkDeliveries,
    isLoading: isLoadingMilkDel,
    isFetching: isFetchingMilkDel,
    refetch: fetchMilkDel
  } = FarmersQuery.getMilkDeliveries(id, {
    ...dataFiltered,
    page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1
  }, dataFiltered.date !== '')

  const {
    data: milkDelData,
    isLoading: isLoadingMilkDelData,
    refetch: fetchMilkDelData
  } = FarmersQuery.getMilkDeliveriesData(id)

  useEffect(() => {
    fetchMilkDel()
    fetchMilkDelData()
    setTableColumnHeaders(getDefaultTableComunHeaders(isSwissDairy))
  }, [t, isSwissDairy])

  useEffect(() => {
    fetchMilkDel()
  }, [dataFiltered])

  useEffect(() => {
    if (milkDelData && !dataFiltered.date) {
      setDataFiltered({
        ...dataFiltered,
        date: milkDelData?.months[0]?.key
      })
    }
  }, [milkDelData])

  function renderMonthFilter() {
    return <>
      <Dropdown
        selectOnBlur={false}
        placeholder={t('Select date')}
        selection
        options={milkDelData?.months?.map(month => ({ text: month.label, value: month.key })) || []}
        value={dataFiltered.date}
        onChange={(_, { value }) => {
          setDataFiltered({
            ...dataFiltered,
            date: value as string
          })
        }}
      />
    </>
  }

  function renderTable() {
    return <TableComponent
      isFetchingData={isFetchingMilkDel}
      animated
      tableColumnHeaders={tableColumnHeaders}
      dataFiltered={dataFiltered}
      handleChangeDataFiltered={(data) => setDataFiltered(data)}
      rows={rows}
      total={filtered}
    />
  }

  const { filtered, rows } = milkDeliveries ? milkDeliveries : { filtered: 0, rows: [] };

  return <Layout
    title={''}
    childContainerSize={'auto'}
    fontSizeHeader={'18px'}
    filters={renderMonthFilter()}
    useSpinner
    isBusy={isLoadingMilkDel || isLoadingMilkDelData}
  >
    {renderTable()}
  </Layout>
}
export default MilkDeliveries